import { has, isEmpty, merge, omit } from 'lodash-es';
import LanguageCodes from 'resources/json/LanguageCodes.json';

/**
 * Converts a numeric form field value to a number, returning a default if the value is a string (e.g. empty string from a cleared input).
 */
export function sanitizeNumericField<T extends number | null>(value: number | string, defaultValue: number): number | T {
   if (typeof value === 'number') return value;

   const parsed = Number(value);
   return isNaN(parsed) ? defaultValue : parsed;
}

type DirtyFields = boolean | null | undefined | Array<any> | Record<string, any>;

interface FormTranslations {
   cache: Record<string, string>;
   language: string | undefined;
   defaultValues?: Record<string, unknown>;
}

const supportedLanguages = new Set(LanguageCodes.map(({ value }) => value));

function getTranslatedFieldKeys(values: Record<string, unknown>) {
   return Object.keys(values).flatMap((fieldName) => {
      const separatorIndex = fieldName.lastIndexOf('_');
      return separatorIndex > 0 && supportedLanguages.has(fieldName.slice(separatorIndex + 1)) ? [fieldName.slice(0, separatorIndex)] : [];
   });
}

export function getTranslatedFieldAliases(values: Record<string, unknown>, fieldKeys: readonly string[], language: string | undefined) {
   return Object.fromEntries(
      fieldKeys.filter((fieldKey) => has(values, `${fieldKey}_${language}`)).map((fieldKey) => [fieldKey, values[`${fieldKey}_${language}`] ?? ''])
   );
}

export function getFormValues<TFormValues extends Record<string, any>>(formValues: TFormValues, translations?: FormTranslations) {
   if (!translations) {
      return formValues;
   }

   const values = merge({}, translations.cache, formValues);
   const language = translations.language;
   const fieldKeys = getTranslatedFieldKeys(values);

   return { ...omit(values, fieldKeys), ...getTranslatedFieldAliases(values, fieldKeys, language) };
}

export function getDirtyFormValues(dirtyFields: DirtyFields, formValues: Record<string, any>, translations?: FormTranslations): any {
   if (translations) {
      const dirtyValues = merge({}, translations.cache, getDirtyFormValues(dirtyFields, formValues) ?? {});
      const unchangedTranslations = Object.keys(dirtyValues).filter((fieldName) => {
         const separatorIndex = fieldName.lastIndexOf('_');
         return (
            separatorIndex > 0 &&
            supportedLanguages.has(fieldName.slice(separatorIndex + 1)) &&
            has(translations.defaultValues, fieldName) &&
            dirtyValues[fieldName] === translations.defaultValues?.[fieldName]
         );
      });
      return getFormValues(omit(dirtyValues, [...getTranslatedFieldKeys(formValues), ...unchangedTranslations]), {
         ...translations,
         cache: {},
      });
   }

   if (dirtyFields == null) {
      return undefined;
   }

   if (dirtyFields === true) {
      return formValues;
   }

   if (Array.isArray(dirtyFields)) {
      return dirtyFields
         .map((dirtyField, index) => getDirtyFormValues(dirtyField, formValues?.[index]))
         .filter((value) => {
            if (value && Object.getPrototypeOf(value) === Object.prototype) {
               return !isEmpty(value);
            }
            if (Array.isArray(value)) {
               return value.filter((val) => !isEmpty(val));
            }
            return true;
         });
   }

   return Object.fromEntries(
      Object.entries(dirtyFields)
         .filter(([key, isDirty]) => formValues && has(formValues, key) && isDirty !== false && !(Array.isArray(isDirty) && isDirty.length === 0))
         .map(([key, value]) => [key, getDirtyFormValues(value, formValues[key])])
         .filter(([, value]) => {
            if (value && Object.getPrototypeOf(value) === Object.prototype) {
               return !isEmpty(value);
            }
            if (Array.isArray(value)) {
               return value.filter((val) => !isEmpty(val));
            }
            return true;
         })
   );
}
